import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { FetchErrorState } from "../components/FetchErrorState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus, 
  Upload, 
  Search, 
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Tag,
  Info,
  Sparkles,
  Zap,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Settings
} from "lucide-react";

// ── Insight Language System + Memory ──

// Classify a section into one of 6 insight states
function classifySection(section) {
  const actual = section.total || 0;
  const planned = Object.values(section.planned).reduce((s, v) => s + v, 0);
  const name = section.name;

  if (planned === 0 && actual === 0) return { type: 'not_started', name, actual, planned, ratio: 0 };
  if (planned === 0 && actual > 0) return { type: 'on_track', name, actual, planned, ratio: 1 };

  const ratio = actual / planned;

  // Income: over = good, under = drift
  if (name === 'Income') {
    if (ratio >= 0.95) return { type: 'on_track', name, actual, planned, ratio };
    if (ratio >= 0.7) return { type: 'slight_drift', name, actual, planned, ratio };
    return { type: 'significant_drift', name, actual, planned, ratio };
  }

  // Expense sections: under budget is good
  if (ratio <= 0.1 && actual < 10) return { type: 'not_started', name, actual, planned, ratio };
  if (ratio <= 0.5) return { type: 'under_utilised', name, actual, planned, ratio };
  if (ratio <= 0.85) return { type: 'positive_progress', name, actual, planned, ratio };
  if (ratio <= 1.15) return { type: 'on_track', name, actual, planned, ratio };
  if (ratio <= 1.4) return { type: 'slight_drift', name, actual, planned, ratio };
  return { type: 'significant_drift', name, actual, planned, ratio };
}

// Priority: significant_drift > positive_progress > slight_drift > under_utilised > not_started > on_track
const INSIGHT_PRIORITY = {
  significant_drift: 0,
  positive_progress: 1,
  slight_drift: 2,
  under_utilised: 3,
  not_started: 4,
  on_track: 5,
};

// ── Variation Pools (3–5 per type+category) ──
// Templates use {name} and {name_lower} for dynamic section names
const INSIGHT_POOLS = {
  // Significant Drift — per category
  'significant_drift:Income': [
    "Your income is tracking a bit below what you'd planned for.",
    "Income hasn't quite reached plan yet — still time in the cycle.",
    "There's a gentle gap between expected and actual income so far.",
  ],
  'significant_drift:Essential Living': [
    "Essentials are taking a little more space than planned this cycle.",
    "Your essential spending has stretched a bit beyond plan.",
    "A bit more is going toward essentials than you'd mapped out.",
  ],
  'significant_drift:Lifestyle': [
    "Lifestyle spending is taking a bit more space this cycle.",
    "A little more is going toward lifestyle than planned.",
    "Lifestyle is stretching slightly beyond what was mapped out.",
  ],
  'significant_drift:Future': [
    "Future contributions are running ahead of plan this cycle.",
    "More is flowing toward future goals than expected.",
    "Your future allocations have moved a little past the plan.",
  ],
  'significant_drift:Debt': [
    "Debt payments are running a bit higher than planned this cycle.",
    "More is going toward debt than you'd mapped out.",
    "Debt repayments have stretched a little beyond the plan.",
  ],

  // Positive Progress
  'positive_progress:Income': [
    "Income is building — you're on a good track.",
    "Income is coming in well — steady momentum.",
    "Your income flow looks healthy this cycle.",
  ],
  'positive_progress:*': [
    "{name} is well within range — you've got room to breathe.",
    "{name} is sitting comfortably below plan — that's healthy space.",
    "There's breathing room in {name_lower} — a good sign.",
  ],

  // Slight Drift
  'slight_drift:Income': [
    "Income is close — just a slight gap from your plan.",
    "Income is nearly there — a small gap to close.",
    "Just a gentle gap between income and plan so far.",
  ],
  'slight_drift:*': [
    "{name} is slightly above plan, but nothing that can't settle.",
    "{name} has edged a little past plan — still manageable.",
    "A small stretch in {name_lower}, but nothing to worry about.",
    "{name} is gently above plan — these things shift.",
  ],

  // Under-utilised
  'under_utilised:*': [
    "You've barely touched {name_lower} this cycle — that space is still there if you need it.",
    "There's room in {name_lower} — it's there when you need it.",
    "{name} has plenty of space left this cycle.",
    "Not much has moved in {name_lower} yet — and that's okay.",
  ],

  // Not Started
  'not_started:*': [
    "{name} hasn't kicked in yet — no rush.",
    "Nothing in {name_lower} yet — that's fine for now.",
    "{name} is quiet this cycle — it'll come when it comes.",
  ],

  // On Track
  'on_track:*': [
    "Everything is tracking close to plan — steady as it goes.",
    "Things are holding well — your plan is working.",
    "Your spending pattern feels balanced this cycle.",
    "No surprises — things are moving as planned.",
  ],

  // Intention-connected (on_track/positive_progress + intention exists)
  'intention:*': [
    "Things are starting to feel a bit more steady this cycle.",
    "Your rhythm is holding — that's the kind of momentum that builds.",
    "The pattern feels calmer this cycle.",
    "There's a sense of steadiness building here.",
  ],
};

// ── Insight Memory (localStorage) ──
const INSIGHT_MEMORY_KEY = 'ara_insight_memory';

function getInsightMemory() {
  try {
    const raw = localStorage.getItem(INSIGHT_MEMORY_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveInsightMemory(type, category, text) {
  try {
    localStorage.setItem(INSIGHT_MEMORY_KEY, JSON.stringify({
      type, category, text, ts: Date.now(),
    }));
  } catch { /* silent */ }
}

function shouldSoftReset(memory) {
  if (!memory || !memory.ts) return true;
  return (Date.now() - memory.ts) / (1000 * 60 * 60) >= 24;
}

// Resolve {name} / {name_lower} template placeholders
function resolveTemplate(template, categoryName) {
  return template
    .replace(/\{name\}/g, categoryName)
    .replace(/\{name_lower\}/g, categoryName.toLowerCase());
}

// Pick a variation with rotation + memory
function pickInsightVariation(type, category, intention) {
  const memory = getInsightMemory();
  const reset = shouldSoftReset(memory);

  // Decide pool key
  let poolKey;
  if (intention && (type === 'on_track' || type === 'positive_progress')) {
    poolKey = 'intention:*';
  } else {
    const specific = `${type}:${category}`;
    poolKey = (specific in INSIGHT_POOLS) ? specific : `${type}:*`;
  }

  const pool = INSIGHT_POOLS[poolKey] || INSIGHT_POOLS['on_track:*'];
  const resolved = pool.map(t => resolveTemplate(t, category));

  // After soft reset or when type/category changed — pick first
  if (reset || !memory || memory.type !== type || memory.category !== category) {
    const text = resolved[0];
    saveInsightMemory(type, category, text);
    return text;
  }

  // Same type + category as last time — rotate to next variation
  const lastIdx = resolved.indexOf(memory.text);
  const nextIdx = lastIdx === -1 ? 0 : (lastIdx + 1) % resolved.length;
  const text = resolved[nextIdx];
  saveInsightMemory(type, category, text);
  return text;
}

// Per-category mini insight (max 5 words)
function getCategoryInsight(actual, planned) {
  if (planned === 0 && actual === 0) return null;
  if (planned === 0) return null; // No plan to compare
  const ratio = actual / planned;
  if (ratio <= 0.1 && actual < 5) return 'Not started yet';
  if (ratio <= 0.5) return 'Room to use';
  if (ratio <= 0.85) return 'Building steadily';
  if (ratio <= 1.15) return 'Tracking well';
  if (ratio <= 1.4) return 'Slightly over';
  return 'Drifting up';
}

// Rotating real-time feedback toasts
const CATEGORIZE_TOASTS = [
  "Nice — that lines up better",
  "That's more in balance",
  "You've brought that back in line",
  "That's a clean adjustment",
  "Sorted — one less thing to think about",
  "Good call — that's clearer now",
];
let _toastIndex = 0;
function getNextCategorizeToast() {
  const msg = CATEGORIZE_TOASTS[_toastIndex % CATEGORIZE_TOASTS.length];
  _toastIndex++;
  return msg;
}

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── 5-section color palettes ──
const SECTION_CONFIG = {
  'Income':           { colors: ['#2d7a5e','#44956f','#5faf84','#7ec89d','#a0dcb8','#c4ecd4','#e0f5ea'], plannedColor: '#a0dcb8', actualColor: '#2d7a5e' },
  'Essential Living': { colors: ['#1a4d3e','#2a6654','#3d7f6b','#559983','#71b29d','#94c9b6','#bddfd2'], plannedColor: '#94c9b6', actualColor: '#1a4d3e' },
  'Lifestyle':        { colors: ['#6b5a3d','#857250','#9e8b66','#b5a47e','#cabc99','#ddd3b8','#ede7d6'], plannedColor: '#ddd3b8', actualColor: '#6b5a3d' },
  'Future':           { colors: ['#1a5d6a','#2d7585','#438d9e','#5da5b6','#7bbccc','#9dd2e0','#c2e6f0'], plannedColor: '#9dd2e0', actualColor: '#1a5d6a' },
  'Debt':             { colors: ['#6b3d3d','#855050','#9e6666','#b58080','#ca9e9e','#ddbcbc','#edd6d6'], plannedColor: '#ddbcbc', actualColor: '#6b3d3d' },
};

// Keyword matching for grouping transactions into sections
const SECTION_KEYWORDS = {
  'Income':           { match: ['salary','freelance','investment returns','other income','interest','dividend','bonus','refund'], isIncome: true },
  'Essential Living': { match: ['groceries','transport','utilities','rent/mortgage','rent','mortgage','insurance','healthcare','health','medical','pharmacy','personal care','fuel','power','internet','water','rates','phone'] },
  'Future':           { match: ['savings deposit','savings','investment','education','kiwisaver','retirement'] },
  'Debt':             { match: ['debt payment','debt','repayment','student loan','loan'] },
  'Lifestyle':        { match: ['dining out','entertainment','subscriptions','shopping','clothing','gifts','travel','hobbies','other expense','other','alcohol','coffee','fitness','gym','streaming'] },
};

// Allocation group_name → section mapping
const ALLOC_GROUP_MAP = {
  'Essential Living': 'Essential Living',
  'Bills': 'Essential Living',
  'Lifestyle': 'Lifestyle',
  'Future': 'Future',
  'Debt': 'Debt',
};

function buildFlowSections(transactions, allocations, debts) {
  // 1. Build sections with actual spend from transactions
  const sections = {};
  for (const key of Object.keys(SECTION_CONFIG)) {
    sections[key] = { name: key, total: 0, subcategories: {}, planned: {}, ...SECTION_CONFIG[key] };
  }

  transactions.forEach(tx => {
    const catName = (tx.category_name || 'Uncategorised').toLowerCase();
    const amount = Math.abs(tx.amount || 0);
    if (amount === 0) return;
    const isIncome = tx.amount > 0;
    let matched = false;

    for (const [sectionName, kw] of Object.entries(SECTION_KEYWORDS)) {
      if (kw.isIncome && !isIncome) continue;
      if (!kw.isIncome && isIncome && sectionName !== 'Income') continue;
      if (kw.isIncome && isIncome) {
        sections[sectionName].total += amount;
        const sub = tx.category_name || 'Other Income';
        sections[sectionName].subcategories[sub] = (sections[sectionName].subcategories[sub] || 0) + amount;
        matched = true; break;
      }
      if (kw.match.some(m => catName.includes(m))) {
        sections[sectionName].total += amount;
        const sub = tx.category_name || 'Uncategorised';
        sections[sectionName].subcategories[sub] = (sections[sectionName].subcategories[sub] || 0) + amount;
        matched = true; break;
      }
    }
    if (!matched && !isIncome) {
      sections['Lifestyle'].total += amount;
      const sub = tx.category_name || 'Uncategorised';
      sections['Lifestyle'].subcategories[sub] = (sections['Lifestyle'].subcategories[sub] || 0) + amount;
    }
  });

  // 2. Map allocations as planned amounts
  (allocations || []).forEach(alloc => {
    const section = ALLOC_GROUP_MAP[alloc.group_name];
    if (section && sections[section]) {
      const name = alloc.name;
      sections[section].planned[name] = (sections[section].planned[name] || 0) + (alloc.amount || 0);
    }
  });

  // 3. Map debts as planned for Debt section
  (debts || []).forEach(debt => {
    const name = debt.name || 'Debt';
    sections['Debt'].planned[name] = (sections['Debt'].planned[name] || 0) + (debt.minimum_payment || 0);
  });

  return Object.values(sections).filter(s => s.total > 0 || Object.keys(s.planned).length > 0);
}

// ── Donut Chart (pure SVG) ──
function DonutChart({ segments, total, colors, size = 120 }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  let offset = 0;
  const shortTotal = total >= 1000 ? `$${(total / 1000).toFixed(1)}k` : `$${Math.round(total)}`;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block mx-auto">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(26,77,62,0.04)" strokeWidth="15" />
        {segments.map((seg, i) => {
          const pct = total > 0 ? seg.amount / total : 0;
          const dashLen = pct * circumference;
          const dashOffset = -offset;
          offset += dashLen;
          return (
            <circle key={seg.name} cx={center} cy={center} r={radius} fill="none"
              stroke={colors[i % colors.length]} strokeWidth="15"
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={dashOffset} strokeLinecap="butt"
              transform={`rotate(-90 ${center} ${center})`}
              className="transition-all duration-500 ease-out"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-[13px] font-bold text-[#1a2b25]">{shortTotal}</span>
      </div>
    </div>
  );
}

// ── Top-of-page Flow Insight (ONE quiet line with memory) ──
function FlowTopInsight({ sections, intention }) {
  const insight = useMemo(() => {
    if (!sections.length) return null;
    const classified = sections
      .filter(s => s.total > 0 || Object.keys(s.planned).length > 0)
      .map(classifySection)
      .sort((a, b) => (INSIGHT_PRIORITY[a.type] ?? 99) - (INSIGHT_PRIORITY[b.type] ?? 99));
    return classified[0] || null;
  }, [sections]);

  // Pick variation with memory (runs once per insight change, not every render)
  const message = useMemo(() => {
    if (!insight) return null;
    return pickInsightVariation(insight.type, insight.name, intention);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insight?.type, insight?.name, intention]);

  if (!insight || !message) return null;

  // Subtle background tint based on type — never alarming
  const tintClass = {
    significant_drift: 'bg-[#6b5a3d]/[0.04]',
    slight_drift: 'bg-[#6b5a3d]/[0.03]',
    positive_progress: 'bg-[#1a4d3e]/[0.04]',
    on_track: 'bg-[#1a4d3e]/[0.03]',
    under_utilised: 'bg-[#1a5d6a]/[0.03]',
    not_started: 'bg-[#1a2b25]/[0.02]',
  }[insight.type] || 'bg-transparent';

  return (
    <div
      className={`rounded-lg px-4 py-2.5 mb-4 ${tintClass} transition-all duration-500`}
      data-testid="flow-top-insight"
    >
      <p className="text-sm text-muted-foreground/80 leading-relaxed" data-testid="flow-top-insight-text">
        {message}
      </p>
    </div>
  );
}

// ── Drill-down Modal — Planned vs Actual Bars (fixed alignment + overflow) ──
function DrillDownModal({ section, open, onClose, formatCurrency }) {
  if (!section) return null;

  // Merge all category names from actual + planned, limit to 8 + Other
  const allCats = new Set([...Object.keys(section.subcategories), ...Object.keys(section.planned)]);
  let allBars = Array.from(allCats).map(name => ({
    name,
    actual: section.subcategories[name] || 0,
    planned: section.planned[name] || 0,
  })).sort((a, b) => Math.max(b.actual, b.planned) - Math.max(a.actual, a.planned));

  // Group overflow into "Other"
  let bars = allBars;
  if (allBars.length > 8) {
    const visible = allBars.slice(0, 7);
    const rest = allBars.slice(7);
    const otherActual = rest.reduce((s, b) => s + b.actual, 0);
    const otherPlanned = rest.reduce((s, b) => s + b.planned, 0);
    bars = [...visible, { name: 'Other', actual: otherActual, planned: otherPlanned }];
  }

  // Shared scale: max across ALL planned AND actual values
  const maxAmt = Math.max(...bars.flatMap(b => [b.actual, b.planned]), 1);
  const hasAnyPlanned = bars.some(b => b.planned > 0);

  // Section-level insight
  const sectionInsight = classifySection(section);
  const sectionInsightLabel = {
    significant_drift: 'Drifting from plan',
    slight_drift: 'Slightly above plan',
    positive_progress: 'Tracking well',
    on_track: 'On track',
    under_utilised: 'Mostly unused',
    not_started: 'Not started',
  }[sectionInsight.type] || null;

  const CHART_H = 160;
  const BAR_W = 14;
  const PAIR_GAP = 4;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" aria-describedby="drill-desc">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            {section.name} Breakdown
            {sectionInsightLabel && hasAnyPlanned && (
              <span className="text-[10px] font-normal text-muted-foreground/60 ml-1" data-testid="drill-section-insight">
                {sectionInsightLabel}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <p id="drill-desc" className="sr-only">Planned vs actual breakdown</p>

        {hasAnyPlanned && (
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: section.plannedColor }} /> Planned
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: section.actualColor }} /> Actual
            </span>
          </div>
        )}

        <div className="pt-2 pb-3 overflow-x-auto" data-testid="drill-down-bars">
          <div className="flex gap-3 px-1" style={{ minWidth: bars.length > 5 ? `${bars.length * 90}px` : 'auto' }}>
            {bars.map((bar) => {
              const plannedPct = maxAmt > 0 ? (bar.planned / maxAmt) * 100 : 0;
              const actualPct = maxAmt > 0 ? (bar.actual / maxAmt) * 100 : 0;
              const catInsight = getCategoryInsight(bar.actual, bar.planned);
              const notStarted = bar.planned > 0 && bar.actual === 0;

              return (
                <div key={bar.name} className="flex-1 flex flex-col items-center" style={{ minWidth: '80px' }}>
                  {/* Value labels — always above bars */}
                  <div className="flex items-center justify-center gap-1 mb-1 h-[28px]">
                    {hasAnyPlanned && (
                      <span className="text-[9px] tabular-nums font-medium whitespace-nowrap" style={{ color: section.plannedColor }}>
                        {formatCurrency(bar.planned)}
                      </span>
                    )}
                    <span className="text-[9px] tabular-nums font-semibold whitespace-nowrap" style={{ color: section.actualColor }}>
                      {formatCurrency(bar.actual)}
                    </span>
                  </div>

                  {/* Bar pair — shared baseline via flex-end */}
                  <div className="flex items-end justify-center" style={{ height: `${CHART_H}px`, gap: `${PAIR_GAP}px` }}>
                    {/* Planned bar — ALWAYS rendered when section has planned data */}
                    {hasAnyPlanned && (
                      <div
                        className="rounded-t-md transition-all duration-500 ease-out"
                        style={{
                          width: `${BAR_W}px`,
                          height: `${Math.max((plannedPct / 100) * CHART_H, bar.planned > 0 ? 3 : 3)}px`,
                          backgroundColor: section.plannedColor,
                          minHeight: '3px',
                        }}
                      />
                    )}
                    {/* Actual bar — ALWAYS rendered */}
                    <div
                      className="rounded-t-md transition-all duration-500 ease-out"
                      style={{
                        width: `${BAR_W}px`,
                        height: `${Math.max((actualPct / 100) * CHART_H, bar.actual > 0 ? 3 : 3)}px`,
                        backgroundColor: section.actualColor,
                        minHeight: '3px',
                      }}
                    />
                  </div>

                  {/* Category label */}
                  <span className="text-[10px] font-medium text-foreground/60 truncate w-full text-center leading-tight mt-1.5">
                    {bar.name}
                  </span>

                  {/* Per-category mini insight */}
                  {notStarted ? (
                    <span className="text-[8px] text-muted-foreground/50 truncate w-full text-center leading-tight"
                      data-testid={`cat-insight-${bar.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      Not started yet
                    </span>
                  ) : catInsight && hasAnyPlanned ? (
                    <span className="text-[8px] text-muted-foreground/50 truncate w-full text-center leading-tight"
                      data-testid={`cat-insight-${bar.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      {catInsight}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Flow Visual Summary (5 pies + top insight + collapsible) ──
function FlowVisualSummary({ sections, formatCurrency, intention, collapsed, onToggle }) {
  const [drillSection, setDrillSection] = useState(null);

  if (!sections.length) return (
    <div className="text-center py-6 text-sm text-muted-foreground/60" data-testid="flow-visual-empty">
      Once your transactions are categorised, this will come to life.
    </div>
  );

  return (
    <div data-testid="flow-visual-summary">
      <FlowTopInsight sections={sections} intention={intention} />

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-[11px] text-muted-foreground/60 hover:text-muted-foreground mb-2 transition-colors"
        data-testid="toggle-pies-btn"
      >
        {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        {collapsed ? 'Show breakdown' : 'Hide breakdown'}
      </button>

      {!collapsed && (
        <>
          <div className={`grid gap-3 ${sections.length <= 3 ? 'grid-cols-2 lg:grid-cols-3' : sections.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3 lg:grid-cols-5'}`}>
            {sections.map((section) => {
              const subs = Object.entries(section.subcategories)
                .map(([name, amount]) => ({ name, amount }))
                .sort((a, b) => b.amount - a.amount);
              let displaySegs = [], otherTotal = 0;
              subs.forEach((s, i) => {
                if (i < 6 && section.total > 0 && (s.amount / section.total) >= 0.04) displaySegs.push(s);
                else otherTotal += s.amount;
              });
              if (otherTotal > 0) displaySegs.push({ name: 'Other', amount: otherTotal });

              const hasPlanned = Object.keys(section.planned).length > 0;
              const plannedTotal = Object.values(section.planned).reduce((s, v) => s + v, 0);

              return (
                <button
                  key={section.name}
                  onClick={() => setDrillSection(section)}
                  className="bg-card border border-border/40 rounded-xl px-2.5 py-3 flex flex-col items-center gap-1.5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                  data-testid={`pie-${section.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className="text-[11px] font-medium text-muted-foreground/70 group-hover:text-foreground/70 transition-colors">{section.name}</p>
                  <DonutChart segments={displaySegs} total={section.total} colors={section.colors} />
                  {hasPlanned && (
                    <p className="text-[9px] text-muted-foreground/50">
                      Planned: {formatCurrency(plannedTotal)}
                    </p>
                  )}
                  <div className="w-full space-y-1">
                    {displaySegs.slice(0, 4).map((seg, i) => (
                      <div key={seg.name} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: section.colors[i % section.colors.length] }} />
                        <span className="truncate flex-1 text-left font-semibold text-foreground/80">{seg.name}</span>
                        <span className="tabular-nums font-semibold text-foreground/70 flex-shrink-0">{formatCurrency(seg.amount)}</span>
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
          <DrillDownModal
            section={drillSection}
            open={!!drillSection}
            onClose={() => setDrillSection(null)}
            formatCurrency={formatCurrency}
          />
        </>
      )}
    </div>
  );
}

/* ── Feed Health Banner ── */
function FeedHealthBanner({ health, onRefresh, refreshing }) {
  if (!health || health.status === "healthy") return null;

  const cfg = {
    stale: { bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-700 dark:text-amber-300", icon: AlertCircle },
    slightly_stale: { bg: "bg-amber-50/60 dark:bg-amber-900/10", text: "text-amber-600 dark:text-amber-300", icon: RefreshCw },
    error: { bg: "bg-red-50/60 dark:bg-red-900/20", text: "text-red-700 dark:text-red-300", icon: AlertCircle },
    disconnected: { bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-600 dark:text-slate-300", icon: Settings },
    never_synced: { bg: "bg-blue-50/60 dark:bg-blue-900/20", text: "text-blue-700 dark:text-blue-300", icon: RefreshCw },
  }[health.status] || { bg: "bg-slate-100", text: "text-slate-600", icon: AlertCircle };

  const Icon = cfg.icon;

  return (
    <div className={`${cfg.bg} rounded-xl px-4 py-3 flex items-center justify-between gap-3 mb-4`} data-testid="feed-health-banner">
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon className={`w-4 h-4 flex-shrink-0 ${cfg.text}`} />
        <div className="min-w-0">
          <p className={`text-sm font-medium ${cfg.text}`} data-testid="feed-health-message">{health.message}</p>
          {health.last_sync && <p className="text-[11px] text-muted-foreground/50">Last synced: {new Date(health.last_sync).toLocaleDateString("en-NZ", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>}
        </div>
      </div>
      <Button size="sm" variant="outline" className="rounded-full flex-shrink-0 text-xs" onClick={onRefresh} disabled={refreshing} data-testid="feed-refresh-btn">
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
        {health.status === "disconnected" ? "Connect" : "Refresh"}
      </Button>
    </div>
  );
}

export function TransactionsContent({ embedded = false }) {
  const { household, accounts } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [feedHealth, setFeedHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  
  // New transaction form
  const [newTx, setNewTx] = useState({
    account_id: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    amount: "",
    category_id: "",
    merchant: "",
    notes: ""
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!household) return;
    fetchData();
    // Fetch feed health
    fetch(`${API_URL}/api/connections/health`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFeedHealth(d); })
      .catch(() => {});
  }, [household]);

  const fetchData = async (retryCount = 0) => {
    if (!household?.household_id) {
      console.warn("[Reconnect] No household_id — skipping fetch");
      return;
    }
    setFetchError(false);
    console.log(`[Reconnect] Fetching transactions for household: ${household.household_id}`);
    try {
      const [txRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/transactions`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' })
      ]);

      if (!txRes.ok) {
        console.error(`[Reconnect] Transactions fetch failed: ${txRes.status}`);
        if (retryCount < 1) {
          console.log("[Reconnect] Silent retry...");
          await new Promise(r => setTimeout(r, 1500));
          return fetchData(retryCount + 1);
        }
        setFetchError(true);
        return;
      }
      const data = await txRes.json();
      console.log(`[Reconnect] Loaded: ${data.transactions?.length || 0} transactions for household: ${household.household_id}`);
      setTransactions(data.transactions || []);

      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(Array.isArray(cats) ? cats : (cats?.categories || []));
      }
    } catch (error) {
      console.error('[Reconnect] Network error:', error);
      if (retryCount < 1) {
        console.log("[Reconnect] Silent retry after network error...");
        await new Promise(r => setTimeout(r, 1500));
        return fetchData(retryCount + 1);
      }
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setFetchError(false);
    console.log("[Reconnect] Manual retry triggered");
    fetchData(0);
  };

  const handleFeedRefresh = async () => {
    if (!household?.household_id) return;
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/akahu/households/${household.household_id}/sync`, { method: "POST", credentials: "include" });
      if (res.ok) {
        toast.success("Sync triggered — refreshing data");
        await fetchData(0);
        const h = await fetch(`${API_URL}/api/connections/health`, { credentials: "include" }).then(r => r.ok ? r.json() : null);
        if (h) setFeedHealth(h);
      } else {
        toast.error("Sync couldn't complete — try reconnecting in Settings");
      }
    } catch {
      toast.error("Couldn't reach the sync service — try reconnecting in Settings");
    } finally { setRefreshing(false); }
  };

  const handleAddTransaction = async () => {
    if (!newTx.account_id || !newTx.description || !newTx.amount) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newTx,
          date: new Date(newTx.date).toISOString(),
          amount: parseFloat(newTx.amount)
        })
      });

      if (!response.ok) throw new Error('Failed to add transaction');

      toast.success(getNextCategorizeToast());
      setShowAddDialog(false);
      setNewTx({
        account_id: "",
        date: new Date().toISOString().split('T')[0],
        description: "",
        amount: "",
        category_id: "",
        merchant: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateTransaction = async (txId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update transaction');

      toast.success(getNextCategorizeToast());
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleQuickCategorize = async (txId, categoryId) => {
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category_id: categoryId, status: 'reviewed' })
      });

      if (!response.ok) throw new Error('Failed to categorize');

      const data = await response.json();
      const catName = data.category_name || categories.find(c => c.category_id === categoryId)?.name || 'Unknown';
      
      if (data.rule_learned) {
        const { keyword, category_name } = data.rule_learned;
        toast(
          <div data-testid="rule-learned-toast">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="font-medium text-sm">Nice — I've learned that '{keyword}' = {category_name}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              I'll auto-categorize this next time.
            </p>
          </div>,
          { duration: 4000 }
        );
      } else {
        const toastMsg = getNextCategorizeToast();
        toast(
          <div className="flex items-center justify-between gap-3" data-testid="categorized-toast">
            <span className="text-sm">{toastMsg}</span>
            <button
              className="text-xs font-medium text-red-500 hover:text-red-600 whitespace-nowrap"
              onClick={() => handleUndoCategorize(txId)}
              data-testid="undo-categorize-btn"
            >
              Undo
            </button>
          </div>,
          { duration: 5000 }
        );
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUndoCategorize = async (txId) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}/undo-categorize`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to undo');
      toast.dismiss();
      toast.success('Category cleared — back to how it was');
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const fetchExplanation = async (tx) => {
    setSelectedTx(tx);
    setExplanation(null);
    setLoadingExplanation(true);
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/transactions/${tx.transaction_id}/explanation`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setExplanation(await res.json());
      }
    } catch { /* silent */ }
    setLoadingExplanation(false);
  };

  const handleConfirmPattern = async (txId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/transactions/${txId}/confirm-pattern`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (data.rule_activated) {
        toast(
          <div data-testid="rule-learned-toast">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="font-medium text-sm">Nice — I've learned that '{data.keyword}' = {data.category_name}</span>
            </div>
            <p className="text-xs text-muted-foreground">I'll auto-categorize this next time.</p>
          </div>,
          { duration: 4000 }
        );
      } else {
        toast.success('Pattern confirmed');
      }
      fetchData();
      // Re-fetch explanation to update panel
      const txRes = await fetch(
        `${API_URL}/api/households/${household.household_id}/transactions/${txId}/explanation`,
        { credentials: 'include' }
      );
      if (txRes.ok) setExplanation(await txRes.json());
    } catch {
      toast.error('Could not confirm pattern. Please try again.');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/transactions/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      
      if (data.imported > 0) {
        toast.success(`${data.imported} transactions uploaded — you're all up to date`);
        fetchData();
      }
      if (data.errors?.length > 0) {
        toast.error(`${data.errors.length} rows had issues — check the file and try again`);
      }
      
      setShowImportDialog(false);
    } catch (error) {
      toast.error("Upload didn't work — please try again");
    }
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = tx.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tx.merchant?.toLowerCase().includes(searchQuery.toLowerCase());
    let matchesStatus = true;
    if (statusFilter === 'imported') matchesStatus = tx.status === 'imported';
    else if (statusFilter === 'categorized') matchesStatus = tx.status === 'reviewed' && !!tx.category_id;
    else if (statusFilter === 'reviewed') matchesStatus = tx.status === 'reviewed';
    else if (statusFilter === 'reconciled') matchesStatus = tx.status === 'reconciled';
    return matchesSearch && matchesStatus;
  });

  // Weekly summary: Money in, Money out, Difference
  const weeklySummary = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    let moneyIn = 0;
    let moneyOut = 0;
    transactions.forEach(tx => {
      const txDate = new Date(tx.date);
      if (txDate >= startOfWeek) {
        if (tx.amount > 0) moneyIn += tx.amount;
        else moneyOut += Math.abs(tx.amount);
      }
    });
    return { moneyIn, moneyOut, difference: moneyIn - moneyOut };
  }, [transactions]);

  // Category spend breakdown for visual layers
  const [allocations, setAllocations] = useState([]);
  const [debts, setDebts] = useState([]);
  const [intention, setIntention] = useState(null);

  // Fetch planned data (allocations + debts) + latest intention
  useEffect(() => {
    if (!household?.household_id) return;
    const hh = household.household_id;
    const headers = { credentials: 'include' };
    Promise.all([
      fetch(`${API_URL}/api/households/${hh}/allocations/`, headers).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/households/${hh}/debts`, headers).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/households/${hh}/journal?entry_type=annual_intention&limit=1`, headers).then(r => r.ok ? r.json() : []),
    ]).then(([allocData, debtData, journalData]) => {
      setAllocations(Array.isArray(allocData) ? allocData : allocData?.allocations || allocData?.items || []);
      const debtItems = Array.isArray(debtData) ? debtData : debtData?.debts || debtData?.items || [];
      setDebts(debtItems);
      // Extract latest intention text
      const entries = Array.isArray(journalData) ? journalData : journalData?.entries || [];
      if (entries.length > 0 && entries[0]?.content) {
        setIntention(entries[0].content);
      }
    }).catch(() => {});
  }, [household?.household_id]);

  const flowSections = useMemo(() => {
    return buildFlowSections(transactions, allocations, debts);
  }, [transactions, allocations, debts]);

  const [txListExpanded, setTxListExpanded] = useState(false);
  const [piesCollapsed, setPiesCollapsed] = useState(false);

  const getStatusBadge = (status, tx) => {
    if (status === 'reviewed' && tx?.category_id) {
      return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">All set</Badge>;
    }
    switch (status) {
      case 'imported':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Needs a quick check</Badge>;
      case 'reviewed':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">All set</Badge>;
      case 'reconciled':
        return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">All set</Badge>;
      case 'duplicate':
        return <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">Looks like a duplicate</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const Wrap = embedded ? ({children}) => <>{children}</> : AppLayout;

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Wrap>
    );
  }

  if (fetchError) {
    return (
      <Wrap>
        <div className="p-6 lg:p-8">
          <FetchErrorState onRetry={handleRetry} message="This looks like a connection issue — your data is safe. Try again." />
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="p-4 lg:p-6" data-testid="transactions-page">
        {/* Feed Health Banner */}
        <FeedHealthBanner health={feedHealth} onRefresh={handleFeedRefresh} refreshing={refreshing} />

        {/* Header — action buttons only (title in tab bar) */}
        <div className="flex items-center justify-end gap-3 mb-4">
          
          <div className="flex items-center gap-3">
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import from bank
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import from bank</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV with columns: date, description, amount, account (name), merchant (optional), notes (optional)
                  </p>
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">
                      Drag and drop or click to select
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleImport}
                      className="hidden"
                      ref={fileInputRef}
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="select-file-btn"
                    >
                      Select File
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-transaction-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add a transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add a transaction</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Account *</Label>
                      <select
                        value={newTx.account_id}
                        onChange={(e) => setNewTx({ ...newTx, account_id: e.target.value })}
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                        data-testid="tx-account-select"
                      >
                        <option value="">Select account</option>
                        {accounts.map(acc => (
                          <option key={acc.account_id} value={acc.account_id}>{acc.display_name || acc.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={newTx.date}
                        onChange={(e) => setNewTx({ ...newTx, date: e.target.value })}
                        data-testid="tx-date-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Input
                      value={newTx.description}
                      onChange={(e) => setNewTx({ ...newTx, description: e.target.value })}
                      placeholder="e.g., Countdown Supermarket"
                      data-testid="tx-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newTx.amount}
                        onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                        placeholder="Use negative for expenses"
                        data-testid="tx-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <select
                        value={newTx.category_id}
                        onChange={(e) => setNewTx({ ...newTx, category_id: e.target.value })}
                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                        data-testid="tx-category-select"
                      >
                        <option value="">Select category</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Merchant</Label>
                    <Input
                      value={newTx.merchant}
                      onChange={(e) => setNewTx({ ...newTx, merchant: e.target.value })}
                      placeholder="e.g., Countdown"
                      data-testid="tx-merchant-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Input
                      value={newTx.notes}
                      onChange={(e) => setNewTx({ ...newTx, notes: e.target.value })}
                      placeholder="Optional notes"
                      data-testid="tx-notes-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddTransaction} className="btn-primary" data-testid="save-transaction-btn">
                      Save
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search your transactions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="sm:w-44" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                {statusFilter === 'all' ? 'All activity' : statusFilter === 'imported' ? 'Needs a quick check' : statusFilter === 'categorized' ? 'Sorted' : statusFilter === 'reviewed' ? 'All set' : statusFilter === 'reconciled' ? 'Confirmed' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                <ChevronDown className="w-4 h-4 ml-auto" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setStatusFilter('all')}>All activity</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('imported')}>Needs a quick check</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('categorized')}>Sorted</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('reviewed')}>All set</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('reconciled')}>Confirmed</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Weekly summary strip */}
        {transactions.length > 0 && (
          <div className="flex items-center justify-between bg-card border border-border rounded-xl px-5 py-2.5 mb-3" data-testid="weekly-summary-strip">
            <span className="text-sm font-medium text-muted-foreground">This week</span>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-[11px] text-muted-foreground/70">Money in</p>
                <p className="text-sm font-semibold tabular-nums text-emerald-600">{formatCurrency(weeklySummary.moneyIn)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-muted-foreground/70">Money out</p>
                <p className="text-sm font-semibold tabular-nums text-foreground">{formatCurrency(weeklySummary.moneyOut)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-muted-foreground/70">Difference</p>
                <p className={`text-sm font-semibold tabular-nums ${weeklySummary.difference >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {weeklySummary.difference >= 0 ? '+' : ''}{formatCurrency(weeklySummary.difference)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Visual Interpretation Layer — Pie Charts ── */}
        {transactions.length > 0 && (
          <div className="mb-3" data-testid="flow-visual-layer">
            <FlowVisualSummary sections={flowSections} formatCurrency={formatCurrency} intention={intention} collapsed={piesCollapsed} onToggle={() => setPiesCollapsed(!piesCollapsed)} />
          </div>
        )}

        {/* ── Transactions List (collapsible, secondary) ── */}
        <div className="flex gap-4">
          <div className={`card-dashboard overflow-hidden transition-all duration-200 relative ${selectedTx ? 'flex-1 min-w-0' : 'w-full'}`}>
            {/* Collapse toggle */}
            {filteredTransactions.length > 0 && (
              <button
                onClick={() => setTxListExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-transactions-list"
              >
                <span>{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}</span>
                {txListExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}

            <div className={`overflow-x-auto transition-all duration-300 ease-out ${
              txListExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-[320px] opacity-80'
            }`}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">What it was</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Where it went</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Review</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr
                    key={tx.transaction_id}
                    className={`ledger-row cursor-pointer ${selectedTx?.transaction_id === tx.transaction_id ? 'bg-slate-50 dark:bg-slate-800/50' : ''}`}
                    data-testid={`tx-row-${tx.transaction_id}`}
                    onClick={() => fetchExplanation(tx)}
                  >
                    <td className="px-4 py-3 text-sm">
                      {new Date(tx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          tx.amount > 0 
                            ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                            : 'bg-stone-100 dark:bg-stone-800/30'
                        }`}>
                          {tx.amount > 0 ? (
                            <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4 text-stone-600 dark:text-stone-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{tx.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {tx.merchant && `${tx.merchant} • `}
                            {accounts.find(a => a.account_id === tx.account_id)?.display_name || accounts.find(a => a.account_id === tx.account_id)?.name || 'Unknown account'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm transition-colors border ${
                                tx.category_id
                                  ? 'border-transparent bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                  : 'border-dashed border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                              }`}
                              data-testid={`category-dropdown-${tx.transaction_id}`}
                            >
                              <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate max-w-[120px]">
                                {tx.category_name || 'Needs a category'}
                              </span>
                              <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-50" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="max-h-64 overflow-y-auto w-48">
                            {tx.category_id && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleUndoCategorize(tx.transaction_id)}
                                  className="text-red-500 focus:text-red-500"
                                  data-testid={`clear-category-${tx.transaction_id}`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Clear category
                                </DropdownMenuItem>
                                <div className="h-px bg-border my-1" />
                              </>
                            )}
                            {categories.map(cat => (
                              <DropdownMenuItem
                                key={cat.category_id}
                                onClick={() => handleQuickCategorize(tx.transaction_id, cat.category_id)}
                                data-testid={`category-option-${cat.category_id}`}
                              >
                                {cat.category_id === tx.category_id && (
                                  <Check className="w-4 h-4 mr-2 text-emerald-600" />
                                )}
                                <span className={cat.category_id === tx.category_id ? 'font-medium' : ''}>
                                  {cat.name}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {tx.auto_categorized && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-400 dark:text-slate-500"
                            data-testid={`auto-badge-${tx.transaction_id}`}
                          >
                            <Check className="w-3 h-3" />
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(tx.status, tx)}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium tabular-nums ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                      {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {tx.status !== 'reviewed' && tx.status !== 'reconciled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => handleUpdateTransaction(tx.transaction_id, { status: 'reviewed' })}
                          data-testid={`mark-reviewed-${tx.transaction_id}`}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          Check
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="text-center py-12" data-testid="empty-transactions">
                <CreditCard className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                <p className="font-medium text-foreground mb-1">Nothing here yet</p>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">Once your accounts are connected or you add a transaction, everything will show up here — clearly and automatically.</p>
                <Button 
                  variant="outline" 
                  className="mt-2"
                  onClick={() => setShowAddDialog(true)}
                  data-testid="add-first-transaction-btn"
                >
                  Add your first transaction
                </Button>
                <p className="text-xs text-muted-foreground/60 mt-3">Or connect your bank to have this update automatically</p>
              </div>
            )}
          </div>
          {/* Collapsed fade overlay */}
          {!txListExpanded && filteredTransactions.length > 5 && (
            <div
              className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none rounded-b-xl"
              style={{ background: 'linear-gradient(transparent, var(--card))' }}
            />
          )}
        </div>

        {/* Detail Side Panel */}
        {selectedTx && (
          <div
            className="w-[340px] flex-shrink-0 card-dashboard overflow-y-auto max-h-[calc(100vh-200px)] animate-in slide-in-from-right-4 duration-200"
            data-testid="tx-detail-panel"
          >
            <div className="p-4 space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{selectedTx.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedTx.merchant && `${selectedTx.merchant} · `}
                    {new Date(selectedTx.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTx(null)}
                  className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground"
                  data-testid="close-detail-panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Amount */}
              <div className={`text-2xl font-bold tabular-nums ${selectedTx.amount > 0 ? 'text-emerald-600' : ''}`}>
                {selectedTx.amount > 0 ? '+' : ''}{formatCurrency(selectedTx.amount)}
              </div>

              {/* Details Grid */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Category</span>
                  <span className="font-medium flex items-center gap-1">
                    {selectedTx.category_name || 'Needs a category'}
                    {selectedTx.auto_categorized && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                        <Check className="w-3 h-3" />Auto
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  {getStatusBadge(selectedTx.status, selectedTx)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Account</span>
                  <span className="font-medium text-xs truncate max-w-[160px]">
                    {accounts.find(a => a.account_id === selectedTx.account_id)?.display_name || accounts.find(a => a.account_id === selectedTx.account_id)?.name || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Why This Was Chosen */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center gap-1.5 mb-3">
                  <Info className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How this was sorted</span>
                </div>

                {loadingExplanation ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Analyzing...
                  </div>
                ) : explanation ? (
                  <div className="space-y-1" data-testid="explanation-content">
                    {/* Type badge */}
                    <div className="mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                        explanation.type === 'auto_categorized' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                        explanation.type === 'income_match' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                        explanation.type === 'manual' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' :
                        'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300'
                      }`} data-testid="explanation-type-badge">
                        {explanation.type === 'auto_categorized' && <><Sparkles className="w-2.5 h-2.5" /> Auto-categorized</>}
                        {explanation.type === 'income_match' && <><Zap className="w-2.5 h-2.5" /> Income match</>}
                        {explanation.type === 'manual' && 'Set manually'}
                        {explanation.type === 'uncategorized' && 'Needs a category'}
                      </span>
                    </div>

                    {/* Reason rows */}
                    {explanation.reasons?.length > 0 ? (
                      explanation.reasons.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 py-1" data-testid={`reason-${i}`}>
                          <div className="w-[72px] flex-shrink-0">
                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                              {r.label || r.type}
                            </span>
                          </div>
                          <div className="flex-1 text-sm">
                            {r.type === 'confidence' ? (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                                r.text === 'high' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                                r.text === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                              }`} data-testid="confidence-badge">
                                {r.text}
                              </span>
                            ) : (
                              <span className="text-slate-700 dark:text-slate-300">{r.text}</span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground italic">{explanation.summary}</p>
                    )}

                    {/* Confirm pattern button */}
                    {explanation.can_confirm && (
                      <div className="mt-3 pt-2 border-t border-border/50">
                        <button
                          onClick={() => handleConfirmPattern(selectedTx.transaction_id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-[#1a4d3e] bg-[#1a4d3e]/5 hover:bg-[#1a4d3e]/10 border border-[#1a4d3e]/20 transition-colors"
                          data-testid="confirm-pattern-btn"
                        >
                          <Check className="w-3 h-3" />
                          Confirm pattern: "{explanation.can_confirm.keyword}" = {explanation.can_confirm.category_name}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic" data-testid="no-explanation">
                    No matching rules yet. Categorise this one and I'll start learning your preferences.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </Wrap>
  );
}

export default function TransactionsPage() {
  return <TransactionsContent />;
}
